import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PageMeta } from "../seo/PageMeta";

function LegalNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navLinks = [
    { label: "Accueil", href: "/" },
    { label: "Nos projets", href: "/projet" },
    { label: "Nous contacter", href: "/contact" },
    { label: "À propos", href: "/about" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-0 transition-all duration-500 ${
        scrolled ? "bg-white/80 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="mx-auto w-full max-w-6xl py-4 md:py-5">
        <div className="flex items-center justify-between md:hidden">
          <a href="/" className="text-sm font-semibold tracking-wide text-[#1a1a2e]">
            LMS
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Ouvrir le menu"
            className="rounded-lg p-2 text-[#1a1a2e] hover:bg-[#1a1a2e]/5"
          >
            <span className="block h-0.5 w-6 bg-current mb-1.5" />
            <span className="block h-0.5 w-6 bg-current mb-1.5" />
            <span className="block h-0.5 w-6 bg-current" />
          </button>
        </div>

        <div className="hidden md:flex justify-center gap-10">
          {navLinks.map(({ label, href }) => (
            <motion.a
              key={label}
              href={href}
              className="relative text-sm font-medium text-[#1a1a2e] tracking-wide"
              whileHover="hover"
              animate="rest"
            >
              {label}
              <motion.span
                className="absolute -bottom-0.5 left-0 h-[1.5px] bg-[#1a1a2e] block"
                variants={{
                  rest: { width: "0%" },
                  hover: { width: "100%" },
                }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </motion.a>
          ))}
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="mt-3 rounded-2xl border border-[#1a1a2e]/10 bg-white/95 p-3 shadow-md md:hidden"
            >
              <div className="flex flex-col">
                {navLinks.map(({ label, href }) => (
                  <a
                    key={label}
                    href={href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-[#1a1a2e] hover:bg-[#1a1a2e]/5"
                  >
                    {label}
                  </a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}

function LegalFooter() {
  return (
    <footer className="bg-[#0C1925] px-6 py-6 text-white/80">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-4 text-sm md:flex-row">
        <p className="text-center text-white/50 md:text-left">
          © {new Date().getFullYear()} LMS Technologies. Tous droits réservés.
        </p>
        <div className="flex w-full items-center justify-center gap-5 text-center lg:w-auto lg:justify-start lg:text-left">
          <a href="/condition" className="text-white/60 transition-colors hover:text-white">
            Conditions d&apos;utilisation
          </a>
          <span className="text-white/25">|</span>
          <a href="/confidentiality" className="text-white/60 transition-colors hover:text-white">
            Politique de confidentialité
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function Condition() {
  return (
    <>
      <PageMeta
        title="Conditions d'utilisation"
        description="Consultez les conditions d'utilisation de LMS Technologies."
        path="/condition"
      />
      <div style={{ fontFamily: "'Inter', sans-serif" }}>
        <LegalNavbar />
        <main className="min-h-screen bg-white px-6 py-24 md:px-12 md:pt-28">
          <section className="mx-auto max-w-4xl">
            <h1 className="text-4xl font-semibold text-[#0C1925] md:text-5xl">Conditions d&apos;utilisation</h1>
            <p className="mt-3 text-sm text-[#1a1a2e]/60 md:text-base">Dernière mise à jour : 6 mai 2026</p>

            <div className="mt-10 space-y-8 text-[#1a1a2e]/85">
              <section>
                <h2 className="text-2xl font-semibold text-[#0C1925]">1. Qui sommes-nous</h2>
                <p className="mt-3 leading-relaxed">
                  LMS Technologies est une agence web québécoise spécialisée en conception, développement et
                  déploiement de sites web et d&apos;applications pour entreprises et professionnels. En naviguant
                  sur ce site, vous acceptez les conditions décrites ci-dessous.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#0C1925]">2. Utilisation du site</h2>
                <p className="mt-3 leading-relaxed">
                  Ce site est mis à disposition à titre informatif pour présenter nos services. Vous vous engagez
                  à l&apos;utiliser de façon légale et respectueuse, sans tenter d&apos;en perturber le fonctionnement,
                  d&apos;en extraire le contenu de manière automatisée, ou de l&apos;utiliser à des fins frauduleuses.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#0C1925]">3. Nos contenus vous appartiennent pas</h2>
                <p className="mt-3 leading-relaxed">
                  Tout ce que vous trouvez sur ce site — textes, visuels, structure, logo et code — est la
                  propriété exclusive de LMS Technologies et est protégé par le droit d&apos;auteur canadien.
                  Aucune reproduction ou réutilisation n&apos;est permise sans notre autorisation écrite préalable.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#0C1925]">4. Nos services en bref</h2>
                <p className="mt-3 leading-relaxed">
                  Nous offrons des services de conception graphique, développement web, hébergement et stratégie
                  numérique. Les détails, tarifs et portées spécifiques à chaque mandat sont toujours formalisés
                  dans un contrat signé entre les deux parties avant le début des travaux.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#0C1925]">5. Liens vers d&apos;autres sites</h2>
                <p className="mt-3 leading-relaxed">
                  Il est possible que notre site contienne des liens vers des ressources ou partenaires externes.
                  Ces sites sont indépendants de LMS Technologies et nous n&apos;assumons aucune responsabilité quant
                  à leur contenu ou leurs pratiques.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#0C1925]">6. Disponibilité du site</h2>
                <p className="mt-3 leading-relaxed">
                  Nous faisons notre possible pour que ce site soit accessible en tout temps, mais nous ne pouvons
                  garantir une disponibilité sans interruption. Des maintenances ou imprévus techniques peuvent
                  occasionnellement rendre le site temporairement inaccessible, sans que cela engage notre
                  responsabilité.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#0C1925]">7. Limite de notre responsabilité</h2>
                <p className="mt-3 leading-relaxed">
                  LMS Technologies ne peut être tenu responsable de dommages directs ou indirects résultant de
                  l&apos;utilisation de ce site, d&apos;une information qui s&apos;y trouve, ou de l&apos;impossibilité d&apos;y
                  accéder. Les informations présentées sont fournies à titre indicatif et peuvent changer sans
                  préavis.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#0C1925]">8. Droit applicable</h2>
                <p className="mt-3 leading-relaxed">
                  Ces conditions sont encadrées par les lois en vigueur au Québec et au Canada. En cas de
                  désaccord ou de litige découlant de l&apos;utilisation de ce site, les tribunaux compétents du
                  Québec auront juridiction exclusive.
                </p>
              </section>
            </div>
          </section>
        </main>
        <LegalFooter />
      </div>
    </>
  );
}
